import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@lib/cn"

export const cardVariants = cva(
  [
    "rounded-xl border bg-white dark:bg-cloverhound-gray-900",
    "border-cloverhound-gray-250 dark:border-cloverhound-gray-800",
    "shadow-xs",
  ].join(" "),
  {
    variants: {
      padding: {
        none: "",
        sm: "p-3",
        md: "p-5",
        lg: "p-8",
      },
      interactive: {
        true: "transition-colors duration-quick ease-out-quad hover:border-cloverhound-gray-400 dark:hover:border-cloverhound-gray-700",
        false: "",
      },
    },
    defaultVariants: { padding: "md", interactive: false },
  },
)

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  as?: React.ElementType
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, padding, interactive, as: Tag = "div", ...props }, ref) => {
    const Component = Tag as React.ElementType
    return <Component ref={ref} className={cn(cardVariants({ padding, interactive }), className)} {...props} />
  },
)
Card.displayName = "Card"

export const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col gap-1", className)} {...props} />
  ),
)
CardHeader.displayName = "CardHeader"

export const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn("text-large font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50", className)}
      {...props}
    />
  ),
)
CardTitle.displayName = "CardTitle"

export const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-small text-cloverhound-gray-700 dark:text-cloverhound-gray-400", className)} {...props} />
  ),
)
CardDescription.displayName = "CardDescription"

export const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("mt-4", className)} {...props} />,
)
CardContent.displayName = "CardContent"

export const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("mt-4 flex items-center gap-2", className)} {...props} />
  ),
)
CardFooter.displayName = "CardFooter"
